import { useEffect } from "react";
import { useStore } from "../state/store";

// Development fraction (members reaching >=34 kt / all members) — the genesis
// proxy. Green = real genesis signal. Lower thresholds than "tracking".
export function genesisColor(developPct: number): string {
  return developPct >= 30 ? "#22C55E" : developPct >= 15 ? "#FBBF24" : "#60A5FA";
}

// NHC's own risk language: low=yellow, medium=orange, high=red.
function nhcRiskColor(risk: string | null): string {
  return risk === "high" ? "#EF4444" : risk === "medium" ? "#FB923C" : "#FBBF24";
}

// NHC's official 7-day formation-chance bins: <40% yellow, 40-60% orange,
// >60% red. Used to colour the GTWO areas on the map, matching their legend.
export function nhcAreaColor(prob7: number | null, risk7: string | null): string {
  if (prob7 != null) return prob7 > 60 ? "#EF4444" : prob7 >= 40 ? "#FB923C" : "#FBE34D";
  if (risk7 === "high") return "#EF4444";
  if (risk7 === "medium") return "#FB923C";
  return "#FBE34D";
}

function fmtPos(lat: number, lon: number): string {
  const ns = lat >= 0 ? "N" : "S";
  const ew = lon >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(1)}°${ns} ${Math.abs(lon).toFixed(1)}°${ew}`;
}

function fmtCycle(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z");
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    hour12: false, timeZone: "UTC",
  }) + "Z";
}

const MAX_ROWS = 8;

/**
 * "What the Euro is watching" — ECMWF ensemble pre-genesis disturbances,
 * ranked by DEVELOPMENT fraction (members reaching TS strength), not raw
 * support (which the ITCZ inflates). NHC's official 7-day genesis % shows
 * alongside for calibration. Clicking a row marks it on the map.
 */
export default function GenesisWatchPanel() {
  const genesis = useStore((s) => s.live.genesis);
  const selected = useStore((s) => s.live.selectedGenesis);
  const showNhcOutlook = useStore((s) => s.live.showNhcOutlook);
  const showDots = useStore((s) => s.live.showGenesisDots);
  const loadGenesis = useStore((s) => s.loadGenesis);
  const selectGenesis = useStore((s) => s.selectGenesis);
  const toggleNhcOutlook = useStore((s) => s.toggleNhcOutlook);
  const toggleGenesisDots = useStore((s) => s.toggleGenesisDots);
  const openOutlookArea = useStore((s) => s.openOutlookArea);

  useEffect(() => {
    if (!genesis) void loadGenesis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watch = genesis?.watch ?? [];
  const nhc = (genesis?.nhc ?? []).filter((a) => a.prob7 != null);
  const hasAreas = (genesis?.gtwo ?? []).some((f) => f.kind === "area");

  return (
    <>
      <hr />
      <label className="muted xs" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>🌱 GENESIS WATCH {watch.length > 0 && `(${watch.length})`}</span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {selected != null && (
            <span style={{ cursor: "pointer", color: "#60A5FA" }} onClick={() => selectGenesis(null)}>
              clear ✕
            </span>
          )}
          {watch.length > 0 && (
            <button
              className={showDots ? "mini-toggle on" : "mini-toggle"} onClick={toggleGenesisDots}
              title="Plot every model genesis signal on the map. NHC's official outlook areas always show; a row you click is always marked."
            >
              map: {showDots ? "on" : "off"}
            </button>
          )}
        </span>
      </label>

      {!genesis && <p className="muted xs" style={{ marginTop: 4 }}>Loading…</p>}
      {genesis && watch.length === 0 && (
        <p className="muted xs" style={{ marginTop: 4 }}>
          No disturbances the ECMWF ensembles are developing in the Atlantic
          or E./C. Pacific right now.
        </p>
      )}

      {watch.slice(0, MAX_ROWS).map((w, i) => {
        const isSel = selected === i;
        return (
          <div
            key={i}
            onClick={() => selectGenesis(isSel ? null : i)}
            title="Click to mark this disturbance on the map"
            style={{
              marginTop: 6, padding: "5px 7px", borderRadius: 6, cursor: "pointer",
              background: isSel ? "rgba(96,165,250,0.16)" : "rgba(255,255,255,0.03)",
              borderLeft: `2px solid ${genesisColor(w.max_develop_pct)}`,
              outline: isSel ? "1px solid rgba(96,165,250,0.5)" : "none",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span>
                <b>{w.basins.join("/")}</b>{" "}
                <span className="muted">{fmtPos(w.lat, w.lon)}</span>
                {w.heading && <span className="muted"> · {w.heading}</span>}
              </span>
              {w.both && <span style={{ color: "#34D399", fontWeight: 700 }} title="Tracked by both the physics (ENS) and AI (AIFS) ensembles">BOTH</span>}
            </div>
            <div style={{ fontSize: 11, marginTop: 2 }}>
              {w.sources.map((s) => (
                <span key={s.source} style={{ marginRight: 10 }}>
                  <span className="muted">{s.source}</span>{" "}
                  <b style={{ color: genesisColor(s.develop_pct) }}>{s.develop_pct}%</b>
                  <span className="muted"> gen · {s.pct}% trk</span>
                </span>
              ))}
            </div>
            {w.nhc7 && w.nhc7.prob != null && (
              <div style={{ fontSize: 11, marginTop: 1 }}>
                <span className="muted">NHC 7-day</span>{" "}
                <b style={{ color: nhcRiskColor(w.nhc7.risk) }}>{w.nhc7.prob}%</b>
                <span className="muted"> {w.nhc7.risk}</span>
              </div>
            )}
            {w.peak_vmax >= 34 && (
              <div className="muted" style={{ fontSize: 10, marginTop: 1 }}>
                ens peak {w.peak_vmax} kt · {w.max_support_pct}% track
              </div>
            )}
          </div>
        );
      })}

      {watch.length > 0 && (
        <p className="muted xs" style={{ marginTop: 6 }}>
          ECMWF ensembles, {fmtCycle(genesis?.cycle ?? null)}. Ranked by{" "}
          <b>genesis %</b> (members reaching ≥34 kt) — a truer signal than{" "}
          <b>track %</b>, which the ITCZ inflates.
        </p>
      )}

      {(hasAreas || nhc.length > 0) && (
        <>
          <label className="muted xs" style={{ marginTop: 12, display: "block" }}>
            🇺🇸 NHC 7-DAY OUTLOOK
          </label>

          {hasAreas && (
            <>
              <button
                className={showNhcOutlook ? "chip active" : "chip"}
                style={{ width: "100%", marginTop: 4 }}
                onClick={toggleNhcOutlook}
              >
                {showNhcOutlook ? "Outlook areas on map: ON" : "Outlook areas on map: OFF"}
              </button>
              <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 10 }} className="muted">
                <span><span style={{ color: "#FBE34D" }}>▣</span> &lt;40%</span>
                <span><span style={{ color: "#FB923C" }}>▣</span> 40–60%</span>
                <span><span style={{ color: "#EF4444" }}>▣</span> &gt;60%</span>
              </div>
            </>
          )}
          {nhc.map((a, i) => {
            // its area on the map (same basin and outlook number): a click opens it there
            const area = (genesis?.gtwo ?? []).find((f) => f.kind === "area" && f.basin === a.basin && f.n === a.n && !!f.id);
            return (
              <div
                key={i} style={{ fontSize: 11, marginTop: 4, cursor: area ? "pointer" : undefined }}
                onClick={area ? () => openOutlookArea(area) : undefined}
                title={area ? "Show this area on the map, with a futurecast of what the models do with it" : undefined}
              >
                <span className="muted">{a.basin}</span>{" "}
                <b style={{ color: nhcRiskColor(a.risk7) }}>{a.prob7}%</b>
                <span className="muted"> 7-day{a.prob48 != null ? ` · 48h ${a.prob48}%` : ""}{a.invest ? ` · ${a.invest}` : ""}</span>
                {area && <span style={{ color: "#60A5FA" }}> · map ›</span>}
                <div className="muted" style={{ fontSize: 10 }}>{a.title}</div>
              </div>
            );
          })}
          <p className="muted xs" style={{ marginTop: 6 }}>
            NHC's official genesis forecast (Atlantic + E. Pacific only — Central
            Pacific is CPHC's). The Euro often leads NHC by days.
          </p>
        </>
      )}
    </>
  );
}
